import React, { forwardRef, useState, useRef, ReactElement } from 'react'
import styled, { css } from 'styled-components'
import { layout, LayoutProps } from 'styled-system'

import { Icon, Flex } from 'components'

import Box from '../Box'
import { ThemeColor } from 'utils/colors'

type AccordionProps = {
  open?: boolean
  children?: React.ReactNode
  classPrefix?: string
  title: React.ReactNode
  customIcon?: React.ReactNode
  customIconRotate?: number
  displayContextMenu?: boolean
  contextMenuIcons?: Array<ReactElement>
  onToggleExpand?: (isOpen: boolean) => void
  onContextMenuClick?: (menuIndex: number, key: React.Key | null) => void
  refreshHeight?: number
  customHeader?: React.ReactNode
  headerHeight?: number
  transition?: string
  iconColor?: ThemeColor
}

type Props = AccordionProps & LayoutProps

const AccordionWrapper = styled.section<LayoutProps & { transition?: string }>`
  ${layout}
  width: 100%;
  box-sizing: border-box;
  ${(p) => (p?.transition ? `transition: ${p.transition};` : '')}
`

const HeaderStyle = css`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  cursor: pointer;
  box-sizing: border-box;
`

const AccordionButton = styled.button`
  ${HeaderStyle}
`

const AccordionHeader = styled.div<{ height?: number }>`
  ${HeaderStyle}
  ${({ height }) => {
    if (height) {
      return css`
        min-height: ${height}px;
        height: ${height}px;
      `
    }
    return css`
      min-height: 50px;
    `
  }}
`

const AccordionContent = styled.div<LayoutProps & { isOpen?: boolean }>`
  ${layout}
  ${({ isOpen }) =>
    !isOpen &&
    css`
      overflow: hidden;
      display: 'none';
      height: 0;
    `};
`

const IconWrapper = styled.div<{ isOpen?: boolean }>`
  transform: ${(p) => (p.isOpen ? 'rotate(0deg)' : 'rotate(90deg)')};
  transition: transform 0.3s;
  line-height: 1;
  display: inline-flex;
  align-items: flex-start;
  width: 16px;
  height: 16px;
`

const ToggleIcon = styled.div<{ isOpen?: boolean; rotate?: number }>`
  transform: ${(p) => (p.isOpen ? `rotate(${p.rotate ? p.rotate : '-180'}deg)` : 'rotate(0deg)')};
  transition: transform 0.3s;

  & > svg {
    margin: 0;
  }
`

const Accordion = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    open,
    children,
    classPrefix,
    title,
    customIcon,
    customIconRotate,
    onToggleExpand,
    displayContextMenu,
    contextMenuIcons,
    onContextMenuClick,
    customHeader,
    headerHeight,
    height,
    transition,
    iconColor,
    ...contentLayoutProps
  } = props
  const [isOpen, setIsOpen] = useState(open)
  const contentRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const clsPre = classPrefix ? `${classPrefix}-` : ''

  React.useEffect(() => {
    setIsOpen(open)
  }, [open])

  const handleClick = () => {
    // updateContentHeight(!isOpen)
    setIsOpen(!isOpen)

    if (onToggleExpand) {
      onToggleExpand(!isOpen)
    }
  }

  /** Temporary comment out the animation as it is buggy */
  // For transition animation, we need to provide an absolute value
  // useEffect(() => {
  //   updateContentHeight(open)
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [])

  // useEffect(() => {
  //   updateContentHeight(isOpen)
  // }, [refreshHeight]) // eslint-disable-line react-hooks/exhaustive-deps

  // JS change the element height for animation purpose.
  // const updateContentHeight = (accordionOpen?: boolean) => {
  //   const ref = contentRef.current

  // if (!ref) return
  // ref.style.maxHeight = accordionOpen ? ref.scrollHeight + 'px' : '0px'
  // }

  const headerOffsetHeight = headerRef?.current?.offsetHeight || 50

  return (
    <AccordionWrapper
      className={`${clsPre}accordion-container`}
      ref={ref}
      {...{
        ...contentLayoutProps,
        height: isOpen ? height : headerOffsetHeight,
        minHeight: isOpen ? contentLayoutProps.minHeight : 0,
        transition,
      }}
    >
      <Flex justifyContent="space-between" ref={headerRef}>
        {!customIcon && (
          <AccordionButton className={`${clsPre}accordion-button`} onClick={handleClick}>
            {title}
            <IconWrapper isOpen={isOpen}>
              <Icon icon="angle-up" {...(iconColor ? { color: iconColor } : {})} />
            </IconWrapper>
          </AccordionButton>
        )}

        {customIcon && (
          <AccordionHeader height={headerHeight} className={`${clsPre}accordion-button`} onClick={handleClick}>
            <Box flexGrow={0}>
              <ToggleIcon isOpen={isOpen} rotate={customIconRotate}>
                {customIcon}
              </ToggleIcon>
            </Box>
            <Box flexGrow={1} marginLeft={2} userSelect="none">
              {title}
            </Box>
            {displayContextMenu && isOpen && !contextMenuIcons && (
              <Box
                flexGrow={0}
                width={20}
                display="flex"
                justifyContent="center"
                onClick={(e) => {
                  onContextMenuClick?.(0, null)
                  e.stopPropagation()
                }}
              >
                <Icon icon={['fa', 'ellipsis-v']} />
              </Box>
            )}
            {displayContextMenu && isOpen && contextMenuIcons && Array.isArray(contextMenuIcons) && (
              <Box className={`${clsPre}accordion-custom-context-menu`} flexGrow={0}>
                {contextMenuIcons.map((item: ReactElement, index) => {
                  if (item) {
                    return React.cloneElement(item, {
                      onClick: (e: React.MouseEvent<HTMLElement>) => {
                        onContextMenuClick?.(index, item.key)
                        e.stopPropagation()
                      },
                      key: item.key || index,
                      ...item.props,
                    })
                  } else {
                    return null
                  }
                })}
              </Box>
            )}
          </AccordionHeader>
        )}

        {customHeader}
      </Flex>
      <AccordionContent
        className={`${clsPre}accordion-content`}
        ref={contentRef}
        isOpen={isOpen}
        height={`calc(100% - ${headerOffsetHeight}px)`}
      >
        {isOpen && children}
      </AccordionContent>
    </AccordionWrapper>
  )
})

export default Accordion
